import React, { useState } from "react";
import { Field } from "formik";
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import BusinessDetailsPopup from "./BusinessDetailsPopup";
import {
  AdminPanelTitle,
  AdminMetric,
  AdminMetadataText,
  AdminTableBody,
  AdminMainText,
} from "../../sections/AdminDashboard/shared/adminUx";

const BusinessDetailsWidget = ({ setFieldValue, values, errors }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  // Define fields with estimated completion times
  const fields = [
    { name: "ABN", key: "abn", estimatedTime: "3 min", required: true },
    { name: "Business name", key: "businessName", estimatedTime: "2 min", required: true },
    { name: "Trade category", key: "industry", estimatedTime: "1 min", required: false }
  ];

  // Sample data for demonstration - complete from start
  const sampleData = {
    businessName: "Acme Plumbing Services",
    industry: "Plumbing",
    abn: "12 345 678 901"
  };

  // Merge sample data with actual values (use sample data when form values are empty or undefined)
  const displayValues = {
    businessName: (values.businessName && values.businessName.trim() !== "") ? values.businessName : sampleData.businessName,
    industry: (values.industry && values.industry.trim() !== "") ? values.industry : sampleData.industry,
    abn: (values.abn && values.abn.trim() !== "") ? values.abn : sampleData.abn
  };

  // Debug logging
  console.log("Values:", values);
  console.log("Sample data:", sampleData);
  console.log("Display values:", displayValues);

  // Calculate completion percentage
  const calculateProgress = () => {
    const completedFields = fields.filter(field => {
      // Check if field has a value (either from form or sample data)
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      
      // Only consider fields completed if they actually have a value
      return hasValue;
    }).length;
    
    return Math.round((completedFields / fields.length) * 100);
  };

  // Calculate estimated time for remaining fields
  const calculateEstimatedTime = () => {
    const remainingFields = fields.filter(field => {
      const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
      return !hasValue;
    });
    
    const totalMinutes = remainingFields.reduce((total, field) => {
      const timeStr = field.estimatedTime;
      const minutes = parseInt(timeStr.replace(' min', ''));
      return total + minutes;
    }, 0);
    
    return totalMinutes > 0 ? `Estimated ${totalMinutes} min` : 'All Completed';
  };

  const progress = calculateProgress();
  const estimatedTime = calculateEstimatedTime();

  return (
    <>
      <DashboardMainContainer className="masonry-item w-[500px] h-auto account-widget">
        <div className="flex items-center justify-between py-3 px-4 border-b border-mediumgray">
          <AdminPanelTitle className="text-offblack">Business Details</AdminPanelTitle>
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 bg-lightgray rounded-full overflow-hidden">
              <div 
                className="h-full bg-freshgreen transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <AdminMetric size="micro" className="text-offblack tabular-nums">
              {progress}%
            </AdminMetric>
          </div>
        </div>
        
        <div className="px-4 py-4">
          {/* Fields List */}
          <div className="space-y-0">
            {fields.map((field, index) => {
              const hasValue = displayValues[field.key] && displayValues[field.key].toString().trim() !== "";
              const isCompleted = hasValue; // Only completed if it actually has a value
              const isPrefilled = ['businessName', 'industry', 'abn'].includes(field.key); // Fields that are prefilled and should be read-only
              
              return (
                <div key={field.key} className="flex items-center justify-between">
            {progress === 100 ? (
              <div 
                className={`group flex items-center w-full py-[4px] ${isPrefilled ? 'cursor-default' : 'cursor-pointer hover:text-primaryblue'} rounded px-2 -mx-2`}
                onClick={isPrefilled ? undefined : () => {
                  setSelectedFieldIndex(index);
                  setIsPopupOpen(true);
                }}
              >
                <AdminTableBody emphasis className={`w-56 text-offblack ${isPrefilled ? '' : 'group-hover:text-primaryblue'}`}>
                  {field.name}
                </AdminTableBody>
                <AdminMainText className={`text-offblack ${isPrefilled ? '' : 'group-hover:text-primaryblue'} truncate`}>
                  {isCompleted ? displayValues[field.key] : `Estimated ${field.estimatedTime}`}
                </AdminMainText>
              </div>
            ) : (
                    <>
                      <div 
                        className={`group flex items-center py-[4px] ${isPrefilled ? 'cursor-default' : 'cursor-pointer hover:text-primaryblue'} rounded px-2 -mx-2 ${index < fields.length - 1 ? 'border-b border-transparent' : ''}`}
                        onClick={isPrefilled ? undefined : () => {
                          setSelectedFieldIndex(index);
                          setIsPopupOpen(true);
                        }}
                      >
                        <div className={`w-2 h-2 rounded-full mr-3 ${isCompleted ? 'bg-freshgreen' : 'bg-mediumgray'}`}></div>
                        <AdminTableBody emphasis className={`w-40 ${isCompleted ? (isPrefilled ? 'text-offblack' : 'text-offblack group-hover:text-primaryblue') : 'text-mediumgray group-hover:text-primaryblue'}`}>
                          {field.name}
                        </AdminTableBody>
                        <AdminMainText className={`text-offblack ${isPrefilled ? '' : 'group-hover:text-primaryblue'} truncate max-w-[200px]`}>
                          {isCompleted ? displayValues[field.key] : ''}
                        </AdminMainText>
                      </div>
                      <AdminMetadataText className="text-mediumgray">
                        {isCompleted ? "Completed" : "Start"}
                      </AdminMetadataText>
                    </>
                  )}
                </div>
              );
            })}
          </div>

        </div>
      </DashboardMainContainer>

      {/* Popup for editing */}
      {isPopupOpen && (
        <BusinessDetailsPopup
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          setFieldValue={setFieldValue}
          values={values}
          errors={errors}
          initialStep={selectedFieldIndex}
        />
      )}
    </>
  );
};

export default BusinessDetailsWidget;
